import { pantryColors, pantryTypography } from '@pantry/ui'
import React, { useState, useRef, useEffect, useMemo } from 'react'
import type { WHLocation } from '../../../../types/warehouse'
import { USER_ROLE_IDS, USER_ROLE_OPTIONS_WITH_CUSTOM } from '../../constants'
import { useUserInvite } from '../../hooks/useUserInvite'
import {
  PANTRY_STORE_PERMISSIONS,
  type PermissionId,
  ROLE_PERMISSION_PRESETS,
} from '../../permissions'
import { getApiErrorMessage } from '../../utils/getApiErrorMessage'
import {
  type SelectedLocations,
  buildLocationRoles,
} from '../../utils/inviteLocationRoles'
import { LocationGrid } from './LocationGrid'
import { PermissionGrid } from './PermissionGrid'
import { FieldError, Label, inputStyle, selectStyle } from './styleHelpers'

const p = pantryColors
const F = pantryTypography.fontFamily

// NOTE: This dialog intentionally diverges from the static mockup at
// `docs/pantry/2026.04.30/breadstack-users.html`. The mockup shows a
// minimal invite form (name + email + role + location). We require the admin
// checkbox + role presets + per-permission overrides + per-location toggles
// (see `frontend/src/pages/Settings/Users/List/InviteDialog`). The current UX
// is kept until the mockup catches up.

interface FormState {
  firstName: string
  lastName: string
  email: string
  isAdmin: boolean
  roleId: string
  permissions: Record<string, boolean>
  locations: SelectedLocations
}

interface FormErrors {
  firstName?: string
  lastName?: string
  email?: string
  roleId?: string
  permissions?: string
  locations?: string
}

// Default role for new invites — matches legacy InviteDialog
// (`usersRole: ROLES.GENERAL_MANAGER` in src/pages/Settings/Users/List/InviteDialog/index.js).
// The permission checkboxes seed from this role's preset so the dialog opens
// with a coherent "General Manager" pre-selection rather than an empty form.
const DEFAULT_INVITE_ROLE_ID = USER_ROLE_IDS.GENERAL_MANAGER

const INITIAL_FORM: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  isAdmin: false,
  roleId: DEFAULT_INVITE_ROLE_ID,
  permissions: presetToMap(
    ROLE_PERMISSION_PRESETS[DEFAULT_INVITE_ROLE_ID] ?? [],
  ),
  locations: { all: true, warehouseIds: [] },
}

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function validate(values: FormState): FormErrors {
  const errors: FormErrors = {}
  if (!values.firstName.trim()) errors.firstName = 'First name is required'
  if (!values.lastName.trim()) errors.lastName = 'Last name is required'
  if (!values.email.trim()) {
    errors.email = 'Email address is required'
  } else if (!validateEmail(values.email.trim())) {
    errors.email = 'Please enter a valid email address'
  }

  // When admin is checked, role / permissions / locations are not part of the
  // payload — skip those validations entirely.
  if (values.isAdmin) return errors

  if (!values.roleId) errors.roleId = 'Please select a role'
  if (values.roleId === USER_ROLE_IDS.CUSTOM_ROLE) {
    const anyChecked = Object.values(values.permissions).some(Boolean)
    if (!anyChecked) {
      errors.permissions = 'Custom role requires at least one permission'
    }
  }
  if (!values.locations.all && values.locations.warehouseIds.length === 0) {
    errors.locations = 'Select at least one location, or choose All locations'
  }
  return errors
}

// Convert a list of permission keys to the boolean map the form keeps in state.
function presetToMap(keys: PermissionId[]): Record<string, boolean> {
  const map: Record<string, boolean> = {}
  for (const k of keys) map[k] = true
  return map
}

// Build the `custom_roles` payload for the backend: every permission the
// system understands → boolean.
function buildCustomRoles(
  permissions: Record<string, boolean>,
): Record<string, boolean> {
  const out: Record<string, boolean> = {}
  for (const k of PANTRY_STORE_PERMISSIONS) out[k] = !!permissions[k]
  return out
}

export interface InviteUserDialogProps {
  open: boolean
  orgId: string
  storeId: string
  warehouses: WHLocation[]
  onClose: () => void
  onSuccess?: () => void
}

export const InviteUserDialog: React.FC<InviteUserDialogProps> = ({
  open,
  orgId,
  storeId,
  warehouses,
  onClose,
  onSuccess,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null)

  const [form, setForm] = useState<FormState>(INITIAL_FORM)
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitted, setSubmitted] = useState(false)
  // Backend (server-side) error surfaced under the form footer — e.g. the
  // 400 "User(s) already exist in organization." response, which is otherwise
  // silently dropped.
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setForm(INITIAL_FORM)
      setErrors({})
      setSubmitted(false)
      setSubmitError(null)
    }
  }, [open])

  const { mutate: invite, isPending } = useUserInvite({
    orgId,
    onSuccess: () => {
      onSuccess?.()
      onClose()
    },
    onError: (err: unknown) => {
      setSubmitError(
        getApiErrorMessage(err, 'Failed to send invitation. Please try again.'),
      )
    },
  })

  // Re-validate while the user edits, but only after they've tried to submit
  // once — keeps the form quiet on first interaction.
  const setField = <K extends keyof FormState>(
    field: K,
    value: FormState[K],
  ) => {
    const next = { ...form, [field]: value } as FormState
    setForm(next)
    if (submitted) setErrors(validate(next))
  }

  const handleAdminToggle = (checked: boolean) => {
    setField('isAdmin', checked)
  }

  const handleRoleChange = (roleId: string) => {
    // When picking a non-custom role, seed the permission checkboxes from the
    // role's preset. Picking 'custom_role'
    // keeps whatever is currently checked.
    const next: FormState = {
      ...form,
      roleId,
      permissions:
        roleId && roleId !== USER_ROLE_IDS.CUSTOM_ROLE
          ? presetToMap(ROLE_PERMISSION_PRESETS[roleId] ?? [])
          : form.permissions,
    }
    setForm(next)
    if (submitted) setErrors(validate(next))
  }

  const handlePermissionToggle = (key: string, checked: boolean) => {
    // Toggling any individual permission switches the role to 'custom_role'
    // — the user has diverged from the preset.
    const nextPermissions = { ...form.permissions, [key]: checked }
    const next: FormState = {
      ...form,
      roleId: USER_ROLE_IDS.CUSTOM_ROLE,
      permissions: nextPermissions,
    }
    setForm(next)
    if (submitted) setErrors(validate(next))
  }

  // Note: a `handlePermissionBulk` helper used to live here for the "Online
  // store" parent-checkbox bulk toggle. The parent checkbox was removed per
  // product request, so the bulk action has no UI trigger anymore.

  const handleLocationsChange = (locations: SelectedLocations) => {
    setField('locations', locations)
  }

  const handleSubmit = () => {
    setSubmitted(true)
    setSubmitError(null)
    const errs = validate(form)
    setErrors(errs)
    if (Object.keys(errs).length > 0) return

    const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim()

    if (form.isAdmin) {
      // Admin invite: backend expects `is_ga: true` and an empty stores array.
      // (The legacy InviteDialog sends `stores: []` for admins; the GA flag is
      // what grants org-wide access.)
      invite({
        organization_id: orgId,
        target_full_name: fullName,
        target_store_id: storeId,
        invite_users: [
          {
            user_email: form.email.trim().toLowerCase(),
            is_ga: true,
            full_name: fullName,
            stores: [],
            channels: [],
          },
        ],
      })
      return
    }

    const locationRoles = buildLocationRoles(form.locations, warehouses)

    // `permissions` is ALWAYS [] — confirmed by:
    //   • legacy InviteDialog (`permissions: []` for every store entry,
    //     regardless of role — see InviteDialog/index.js handleSubmit), and
    //   • the working invite curls (general_manager + custom_role both send []).
    // The backend derives the active permission set from `role_id` (built-in
    // roles like general_manager, marketing_manager, etc.) or from
    // `custom_roles` (when role_id === 'custom_role'). The `permissions` array
    // is a legacy field that's no longer authoritative.
    invite({
      organization_id: orgId,
      target_full_name: fullName,
      target_store_id: storeId,
      invite_users: [
        {
          user_email: form.email.trim().toLowerCase(),
          is_ga: false,
          full_name: fullName,
          stores: [
            {
              store_id: storeId,
              role_id: form.roleId,
              permissions: [],
              location_roles: locationRoles,
              custom_roles:
                form.roleId === USER_ROLE_IDS.CUSTOM_ROLE
                  ? buildCustomRoles(form.permissions)
                  : {},
            },
          ],
          channels: [],
        },
      ],
    })
  }

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose()
  }

  // Memo the role-select options so React doesn't think the array changed
  // every render (keeps the <select> stable).
  const roleOptions = useMemo(() => USER_ROLE_OPTIONS_WITH_CUSTOM, [])

  if (!open) return null

  const btnBase: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    cursor: isPending ? 'not-allowed' : 'pointer',
    border: 'none',
    fontFamily: F,
    transition: 'all .15s',
    opacity: isPending ? 0.7 : 1,
  }

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.35)',
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 10,
          width: 620,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,.15)',
          fontFamily: F,
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${p.gray[200]}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: p.gray[800],
              fontFamily: F,
            }}
          >
            Invite New User
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              border: 'none',
              background: 'transparent',
              color: p.gray[400],
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all .15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = p.gray[100]
              e.currentTarget.style.color = p.gray[700]
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = p.gray[400]
            }}
          >
            <svg
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              width="20"
              height="20"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 24 }}>
          {/* Row: First Name + Last Name */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              marginBottom: 16,
            }}
          >
            <div>
              <Label required>First Name</Label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setField('firstName', e.target.value)}
                placeholder="Jane"
                style={inputStyle(!!errors.firstName)}
              />
              <FieldError msg={errors.firstName} />
            </div>
            <div>
              <Label required>Last Name</Label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setField('lastName', e.target.value)}
                placeholder="Doe"
                style={inputStyle(!!errors.lastName)}
              />
              <FieldError msg={errors.lastName} />
            </div>
          </div>

          {/* Email */}
          <div style={{ marginBottom: 16 }}>
            <Label required>Email Address</Label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              placeholder="jane.doe@company.com"
              style={inputStyle(!!errors.email)}
            />
            <FieldError msg={errors.email} />
            {!errors.email && (
              <div
                style={{
                  fontSize: 12,
                  color: p.gray[400],
                  marginTop: 4,
                  fontFamily: F,
                }}
              >
                An invitation link will be sent to this email.
              </div>
            )}
          </div>

          {/* Admin toggle — replicates the legacy "is_ga" checkbox */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 12px',
              background: p.gray[50],
              borderRadius: 6,
              marginBottom: 16,
            }}
          >
            <input
              id="invite-admin-toggle"
              type="checkbox"
              checked={form.isAdmin}
              onChange={(e) => handleAdminToggle(e.target.checked)}
              style={{
                width: 16,
                height: 16,
                accentColor: '#2563EB',
                cursor: 'pointer',
                margin: 0,
              }}
            />
            <label
              htmlFor="invite-admin-toggle"
              style={{
                fontSize: 13.5,
                fontWeight: 600,
                color: p.gray[700],
                fontFamily: F,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              Admin
            </label>
            <span
              style={{
                fontSize: 12,
                color: p.gray[400],
                fontFamily: F,
                marginLeft: 4,
              }}
            >
              Grants full org-wide access. Skips the role / permission /
              location settings below.
            </span>
          </div>

          {/* Role / permission / location section — hidden when Admin is checked */}
          {!form.isAdmin && (
            <>
              <div style={{ marginBottom: 16 }}>
                <Label required>Role</Label>
                <select
                  value={form.roleId}
                  onChange={(e) => handleRoleChange(e.target.value)}
                  style={selectStyle(!!errors.roleId)}
                >
                  <option value="" disabled>
                    Select a role
                  </option>
                  {roleOptions.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <FieldError msg={errors.roleId} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <PermissionGrid
                  selected={form.permissions}
                  errorMsg={errors.permissions}
                  onToggleOne={handlePermissionToggle}
                />
              </div>

              <div>
                <LocationGrid
                  warehouses={warehouses}
                  value={form.locations}
                  onChange={handleLocationsChange}
                  errorMsg={errors.locations}
                />
              </div>
            </>
          )}
        </div>

        {/* Server-side error banner */}
        {submitError && (
          <div
            role="alert"
            style={{
              margin: '0 24px 16px',
              padding: '10px 12px',
              background: p.red[50],
              border: `1px solid ${p.red[200]}`,
              borderRadius: 6,
              color: p.red[700],
              fontSize: 13,
              fontFamily: F,
            }}
          >
            {submitError}
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: `1px solid ${p.gray[200]}`,
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            style={{
              ...btnBase,
              background: 'transparent',
              color: p.gray[600],
              border: `1px solid ${p.gray[300]}`,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isPending}
            style={{ ...btnBase, background: '#2563EB', color: '#fff' }}
          >
            {isPending ? (
              <>
                <svg
                  style={{ animation: 'spin 1s linear infinite' }}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                >
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
                <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
                Sending…
              </>
            ) : (
              'Send Invitation'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
